import * as React from 'react';
import { useState } from 'react';

import { Modal, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import useCreateDemoGym from 'hooks/demo-gym/useCreateDemoGym';
import { CountryEnum } from 'types/enums';
import { DemoGymList, DemoGymWrite } from 'types/generated';
import { emailValidator, nameValidator } from 'utils/form-validators';
import notificationHandler from 'utils/notifications';

import CreateDemoGymBaseForm from './CreateDemoGymBaseForm';
import CreateDemoGymPrefilledDataForm from './CreateDemoGymPrefilledDataForm';
import { CreateDemoGymFormValues } from './types';

const CreateDemoGymModal = ({
  opened,
  handleClose,
  demoGymTypeToCreate,
}: {
  opened: boolean;
  handleClose: () => void;
  demoGymTypeToCreate: DemoGymList['type'];
}): JSX.Element => {
  const { successNotification, errorNotification } = notificationHandler();

  const [formStep, setFormStep] = useState<0 | 1>(0);

  const createDemoGymMutation = useCreateDemoGym();

  const form = useForm<CreateDemoGymFormValues>({
    initialValues: {
      title: '',
      gymType: null,
      timezone: '',
      firstName: '',
      lastName: '',
      email: '',
      country: CountryEnum.USA,
      demoType: 'blank',
    },

    validate: {
      title: value => (value.trim() ? null : 'Gym title is required'),
      gymType: value => (value ? null : 'Gym type is required'),
      firstName: value => nameValidator(value),
      lastName: value => nameValidator(value),
      email: value => (value ? emailValidator(value) : 'Email is required'),
      timezone: value => (value ? null : 'Timezone is required'),
    },
  });

  const handleSubmit = (): void => {
    const payload: DemoGymWrite = {
      gymTitle: form.values.title,
      gymType: form.values.gymType as DemoGymWrite['gymType'],
      timezone: form.values.timezone as DemoGymWrite['timezone'],
      country: form.values.country,
      ownerEmail: form.values.email as string,
      ownerFirstName: form.values.firstName,
      ownerLastName: form.values.lastName,
      type: demoGymTypeToCreate,
      prepopulatedGymType: form.values.demoType === 'pre-filled' ? form.values.gymType! : '',
    };

    createDemoGymMutation.mutate(payload, {
      onSuccess: () => {
        successNotification('Demo gym created successfully!');
        handleClose();
        form.reset();
        setFormStep(0);
      },
      onError: error => {
        errorNotification('Failed to create demo gym. Please try again.', error?.response?.data.message);
      },
    });
  };

  const handleModalClose = (): void => {
    handleClose();
    form.reset();
    setFormStep(0);
  };

  const handleStepForward = (): void => {
    const validation = form.validate();
    if (!validation.hasErrors) {
      setFormStep(1);
    }
  };

  return (
    <Modal
      centered
      opened={opened}
      onClose={handleModalClose}
      title={<Title order={3}>New Demo Gym</Title>}
      size='lg'
    >
      {formStep === 0 && (
        <CreateDemoGymBaseForm
          form={form}
          handleClose={handleModalClose}
          handleStepForward={handleStepForward}
        />
      )}
      {formStep === 1 && (
        <CreateDemoGymPrefilledDataForm
          form={form}
          handleBackward={() => setFormStep(0)}
          handleSubmit={handleSubmit}
          isLoading={createDemoGymMutation.isLoading}
        />
      )}
    </Modal>
  );
};

export default CreateDemoGymModal;
